import { AxisType, ChartConfigMode, } from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import { CustomChartAxisConfigTypeEnum, CustomChartDimensionModeEnum, } from '../types/prism';
import { mapAnswerColumnToCustomColumns } from './answer-column-adaptor.util';
const NOT_VISUALIZED_KEY = 'notVisualized';
const convertCustomChartDimensionToChartConfigDimension = (dimension, columnMap) => {
    if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
        return {
            key: dimension.key,
            mode: ChartConfigMode.AXIS_DRIVEN,
            axes: dimension.axes?.map(axis => {
                if (axis.__typename === 'FlatAxis') {
                    const column = columnMap[axis.column];
                    return column ? {
                        type: AxisType.FLAT,
                        column: column,
                    } : null;
                }
                else if (axis.__typename === 'MergedAxis') {
                    const columns = axis.columns.map((col) => columnMap[col]).filter(Boolean);
                    return columns.length > 0 ? {
                        type: AxisType.MERGED,
                        columns: columns,
                    } : null;
                }
                else if (axis.__typename === 'DualAxis') {
                    let primary = null;
                    let secondary = null;
                    if (axis.primary.__typename === 'FlatAxis') {
                        const column = columnMap[axis.primary.column];
                        if (!column) {
                            return null;
                        }
                        primary = {
                            type: AxisType.FLAT,
                            column: column,
                        };
                    }
                    else if (axis.primary.__typename === 'MergedAxis') {
                        const columns = axis.primary.columns.map((col) => columnMap[col]).filter(Boolean);
                        if (columns.length === 0) {
                            return null;
                        }
                        primary = {
                            type: AxisType.MERGED,
                            columns: columns,
                        };
                    }
                    if (axis.secondary.__typename === 'FlatAxis') {
                        const column = columnMap[axis.secondary.column];
                        if (!column) {
                            return null;
                        }
                        secondary = {
                            type: AxisType.FLAT,
                            column: column,
                        };
                    }
                    else if (axis.secondary.__typename === 'MergedAxis') {
                        const columns = axis.secondary.columns.map((col) => columnMap[col]).filter(Boolean);
                        if (columns.length === 0) {
                            return null;
                        }
                        secondary = {
                            type: AxisType.MERGED,
                            columns: columns
                        };
                    }
                    return {
                        type: AxisType.DUAL,
                        primary,
                        secondary,
                    };
                }
                return null;
            }).filter(Boolean) ?? [],
        };
    }
    else {
        return {
            key: dimension.key,
            mode: ChartConfigMode.COLUMN_DRIVEN,
            columns: dimension.columns
                ? dimension.columns.map(colId => {
                    return columnMap[colId];
                })
                : [],
        };
    }
};
const convertChartConfigDimensionToInput = (dimension) => {
    if (dimension.mode === ChartConfigMode.AXIS_DRIVEN) {
        return {
            key: dimension.key,
            mode: CustomChartDimensionModeEnum.AxisDriven,
            axes: dimension.axes?.map(axis => {
                if (axis.type === AxisType.FLAT) {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Flat,
                        column: axis.column.id,
                    };
                }
                else if (axis.type === AxisType.MERGED) {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Merged,
                        columns: axis.columns.map(col => col.id),
                    };
                }
                else if (axis.type === AxisType.DUAL) {
                    let primary = null;
                    let secondary = null;
                    if (axis.primary.type === AxisType.FLAT) {
                        primary = {
                            type: CustomChartAxisConfigTypeEnum.Flat,
                            column: axis.primary.column.id,
                        };
                    }
                    else if (axis.primary.type === AxisType.MERGED) {
                        primary = {
                            type: CustomChartAxisConfigTypeEnum.Merged,
                            columns: axis.primary.columns.map(col => col.id),
                        };
                    }
                    if (axis.secondary.type === AxisType.FLAT) {
                        secondary = {
                            type: CustomChartAxisConfigTypeEnum.Flat,
                            column: axis.secondary.column.id,
                        };
                    }
                    else if (axis.secondary.type === AxisType.MERGED) {
                        secondary = {
                            type: CustomChartAxisConfigTypeEnum.Merged,
                            columns: axis.secondary.columns.map(col => col.id),
                        };
                    }
                    return {
                        type: CustomChartAxisConfigTypeEnum.Dual,
                        primary,
                        secondary,
                    };
                }
                return null;
            }) ?? [],
        };
    }
    else {
        return {
            key: dimension.key,
            mode: CustomChartDimensionModeEnum.ColumnDriven,
            columns: dimension.columns?.map(col => col.id) ?? [],
        };
    }
};
const convertChartConfigDimensionToCustomChartDimension = (dimension) => {
    if (dimension.mode === ChartConfigMode.AXIS_DRIVEN) {
        return {
            key: dimension.key,
            axes: dimension.axes?.map(axis => {
                if (axis.type === AxisType.FLAT) {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Flat,
                        column: axis.column.id,
                        __typename: 'FlatAxis',
                    };
                }
                else if (axis.type === AxisType.MERGED) {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Merged,
                        columns: axis.columns.map(col => col.id),
                        __typename: 'MergedAxis',
                    };
                }
                else if (axis.type === AxisType.DUAL) {
                    let primary = null;
                    let secondary = null;
                    if (axis.primary.type === AxisType.FLAT) {
                        primary = {
                            type: CustomChartAxisConfigTypeEnum.Flat,
                            column: axis.primary.column.id,
                            __typename: 'FlatAxis',
                        };
                    }
                    else if (axis.primary.type === AxisType.MERGED) {
                        primary = {
                            type: CustomChartAxisConfigTypeEnum.Merged,
                            columns: axis.primary.columns.map(col => col.id),
                            __typename: 'MergedAxis',
                        };
                    }
                    if (axis.secondary.type === AxisType.FLAT) {
                        secondary = {
                            type: CustomChartAxisConfigTypeEnum.Flat,
                            column: axis.secondary.column.id,
                            __typename: 'FlatAxis',
                        };
                    }
                    else if (axis.secondary.type === AxisType.MERGED) {
                        secondary = {
                            type: CustomChartAxisConfigTypeEnum.Merged,
                            columns: axis.secondary.columns.map(col => col.id),
                            __typename: 'MergedAxis',
                        };
                    }
                    return {
                        type: CustomChartAxisConfigTypeEnum.Dual,
                        primary,
                        secondary,
                        __typename: 'DualAxis',
                    };
                }
                return null;
            }) ?? [],
            __typename: 'AxisDrivenChartConfigDimension',
        };
    }
    else {
        return {
            key: dimension.key,
            columns: dimension.columns?.map(col => col.id) ?? [],
            __typename: 'ColumnDrivenChartConfigDimension',
        };
    }
};
const convertCustomChartDimensionToInput = (dimension) => {
    if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
        return {
            key: dimension.key,
            mode: CustomChartDimensionModeEnum.AxisDriven,
            axes: dimension.axes?.map(axis => {
                if (axis.__typename === 'FlatAxis') {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Flat,
                        column: axis.column,
                    };
                }
                if (axis.__typename === 'MergedAxis') {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Merged,
                        columns: axis.columns,
                    };
                }
                if (axis.__typename === 'DualAxis') {
                    return {
                        type: CustomChartAxisConfigTypeEnum.Dual,
                        primary: axis.primary.__typename === 'FlatAxis'
                            ? {
                                type: CustomChartAxisConfigTypeEnum.Flat,
                                column: axis.primary.column,
                            }
                            : {
                                type: CustomChartAxisConfigTypeEnum.Merged,
                                columns: axis.primary.columns,
                            },
                        secondary: axis.secondary.__typename === 'FlatAxis'
                            ? {
                                type: CustomChartAxisConfigTypeEnum.Flat,
                                column: axis.secondary.column,
                            }
                            : {
                                type: CustomChartAxisConfigTypeEnum.Merged,
                                columns: axis.secondary.columns,
                            },
                    };
                }
                return null;
            }) ?? [],
        };
    }
    return {
        key: dimension.key,
        mode: CustomChartDimensionModeEnum.ColumnDriven,
        columns: dimension.columns ?? [],
    };
};
export const parseChartConfigToCustomChartConfigInput = (customChartConfig) => {
    if (_.isEmpty(customChartConfig)) {
        return [];
    }
    return customChartConfig.map((config) => {
        return {
            key: config.key,
            dimensions: config.dimensions?.map(convertChartConfigDimensionToInput) ?? [],
        };
    });
};
export const parseCustomChartConfigToCustomChartConfigInput = (customChartConfig) => {
    if (_.isEmpty(customChartConfig)) {
        return null;
    }
    return customChartConfig.map((config) => {
        return {
            key: config.key,
            dimensions: config.dimensions
                ? config.dimensions
                    .filter(dim => dim.key !== NOT_VISUALIZED_KEY)
                    .map(convertCustomChartDimensionToInput)
                : [],
        };
    });
};
export const mapCustomChartConfigToSDKConfig = (customChartConfig, allColumns, columnProperties = []) => {
    const columnMap = _.reduce(allColumns, (acc, col) => {
        const column = col.column;
        const columnProp = columnProperties.find(prop => prop.columnId === column.id) ??
            {};
        acc[col.column.id] = mapAnswerColumnToCustomColumns(column, columnProp?.columnProperty);
        return acc;
    }, {});
    return customChartConfig?.map(config => {
        return {
            key: config.key,
            dimensions: config.dimensions
                ? config.dimensions.map(dim => {
                    return convertCustomChartDimensionToChartConfigDimension(dim, columnMap);
                })
                : [],
        };
    });
};
export const areChartConfigEqual = (customChartConfig1, customChartConfig2) => {
    if (_.isNil(customChartConfig1) && _.isNil(customChartConfig2)) {
        return true;
    }
    if (_.isNil(customChartConfig1) || _.isNil(customChartConfig2)) {
        return false;
    }
    if (customChartConfig1.length !== customChartConfig2.length) {
        return false;
    }
    const areConfigEqual = customChartConfig1.every(config1 => {
        const config2 = customChartConfig2.find(config => config.key === config1.key);
        if (_.isEqual(_.omit(config1, '__typename'), _.omit(config2, '__typename'))) {
            return true;
        }
        return false;
    });
    return areConfigEqual;
};
export const mapSDKConfigToCustomChartConfig = (chartConfigList) => {
    if (_.isEmpty(chartConfigList)) {
        return [];
    }
    return chartConfigList?.map(config => {
        return {
            key: config.key,
            dimensions: config.dimensions
                ? config.dimensions.map(convertChartConfigDimensionToCustomChartDimension)
                : [],
        };
    });
};
export const isConditionalFormattingEqual = (currentColumnProperties, prevColumnProperties) => {
    const currentCfRules = currentColumnProperties.map(prop => prop.columnProperty.conditionalFormatting);
    const prevCfRules = prevColumnProperties.map(prop => prop.columnProperty.conditionalFormatting);
    return _.isEqual(currentCfRules, prevCfRules);
};
//# sourceMappingURL=custom-chart-config-adaptor.util.js.map